import { forwardRef, useRef, useState } from 'react';
import { Box, Button, Checkbox, Collapse, Stack, Typography } from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Link } from 'react-router-dom';
import formatDate from '../../../../utils/formatDate';
import { TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED } from '../styles';
import {
    SelectionStatus,
    useInvoicingSelectionContext,
} from '../../../Contexts/InvoicingSelectionContext';
import HierarchyConnectorHorizontal from '../HierarchyConnectorHorizontal';
import AnimatedExpandIcon from '../../../../Shared/Components/AnimatedExpandIcon';
import useHierarchyConnectorVertical from '../hooks/useHierarchyConnectorVertical';
import LocationEntries from './LocationEntries';
import { COLORS } from '../../../../utils/Application_Constants';

const CHILD_PADDING = 6;

const InvoicingItemContainer = (
    {
        hierarchyConnectorVerticalRef: parentHierarchyConnectorVerticalRef,
        bookingId,
        type,
        itemId,
        data,
        selectable = false,
    },
    ref
) => {
    const connectorParentRef = useRef(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const toggleExpand = () => {
        setIsExpanded((prevValue) => !prevValue);
    };

    const {
        mainContainerRef,
        expandIconRef,
        lastChildRef,
        hierarchyConnectorVerticalRef,
        renderConnector,
    } = useHierarchyConnectorVertical();

    const { getBookingItemSelectionState, handleSelectItem } = useInvoicingSelectionContext();
    const selectionStatus = getBookingItemSelectionState(bookingId, type, itemId);
    const hasSubItems = type === 'timesheet' && data.locations?.length > 0;

    return (
        <Box ref={mainContainerRef} position="relative">
            <Stack
                ref={ref}
                direction="row"
                alignItems="center"
                height={80}
                spacing={1}
                sx={{ cursor: hasSubItems ? 'pointer' : 'default', pr: CHILD_PADDING }}
                onClick={hasSubItems ? toggleExpand : undefined}
                aria-expanded={hasSubItems ? isExpanded : undefined}
                aria-controls={hasSubItems ? `subitem-collapse-${itemId}` : undefined}
                gap="60px"
            >
                <Box
                    ref={connectorParentRef}
                    sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        position: 'relative',
                        marginLeft: !hasSubItems ? '31px' : undefined,
                    }}
                >
                    <HierarchyConnectorHorizontal
                        anchorRef={connectorParentRef}
                        verticalRef={parentHierarchyConnectorVerticalRef}
                    />
                    {hasSubItems && (
                        <AnimatedExpandIcon ref={expandIconRef} isExpanded={isExpanded} />
                    )}
                    {selectable && (
                        <Checkbox
                            checked={selectionStatus === SelectionStatus.SELECTED}
                            indeterminate={selectionStatus === SelectionStatus.INDETERMINATE}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleSelectItem(bookingId, type, itemId);
                            }}
                        />
                    )}
                    {type === 'timesheet' && (
                        <Stack direction="row" alignItems="center" gap={2}>
                            <Stack gap={1} sx={{ maxWidth: 208, pl: CHILD_PADDING / 2 }}>
                                <Typography sx={TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED}>
                                    Timesheet
                                </Typography>
                                <Typography
                                    sx={{
                                        ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED,
                                        fontSize: 12,
                                        fontStyle: 'italic',
                                    }}
                                >
                                    Locations: {data.locations?.length || 0}
                                </Typography>
                            </Stack>
                            {data.hasErrors && (
                                <InfoOutlinedIcon sx={{ color: COLORS.LT_PEPPER }} />
                            )}
                        </Stack>
                    )}
                </Box>
                {type === 'timesheet' && (
                    <>
                        <Typography
                            sx={{
                                ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED,
                                fontSize: 12,
                                fontWeight: 400,
                                '& span': { fontSize: 14, fontWeight: 600 },
                            }}
                        >
                            Week ending:&nbsp;
                            <span>{formatDate(data.weekEnding)}</span>
                        </Typography>
                        <Typography
                            sx={{
                                ...TABLE_CELL_TYPOGRAPHY_STYLES_INVOICED,
                                fontSize: 12,
                                fontWeight: 400,
                                lineHeight: '16px',
                                '& span': { fontSize: 14 },
                            }}
                        >
                            Totals to: Bill:&nbsp;
                            <span>${data.totals?.totalToBill.toFixed(2)}</span>
                            &nbsp;/ Pay:&nbsp;
                            <span>${data.totals?.totalToPay.toFixed(2)}</span>
                        </Typography>
                        <Stack direction="row" gap={3}>
                            <Button
                                variant="text"
                                component={Link}
                                to="/timesheets"
                                target="_blank"
                                size="small"
                                startIcon={<LaunchIcon />}
                                sx={{ p: 0.5, fontSize: 12 }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                Raw timesheet
                            </Button>
                            <Button
                                variant="text"
                                component={Link}
                                to={`/processed-timesheets/${itemId}`}
                                target="_blank"
                                size="small"
                                startIcon={<LaunchIcon />}
                                sx={{ p: 0.5, fontSize: 12 }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                Processed timesheet
                            </Button>
                        </Stack>
                    </>
                )}
            </Stack>

            {hasSubItems && (
                <Collapse in={isExpanded} id={`subitem-collapse-${itemId}`}>
                    {renderConnector(isExpanded)}
                    <Stack sx={{ px: CHILD_PADDING, mt: '10px' }} gap="30px">
                        {data?.locations?.map((location) => (
                            <LocationEntries
                                key={location.locationId}
                                bookingId={bookingId}
                                timesheetId={itemId}
                                locationName={location.name}
                                errorMessage={location.errorMessage}
                                lineItems={location.lineItems}
                                hierarchyConnectorVerticalRef={hierarchyConnectorVerticalRef}
                                lastChildRef={lastChildRef}
                                selectable={selectable}
                            />
                        ))}
                    </Stack>
                </Collapse>
            )}
        </Box>
    );
};

export default forwardRef(InvoicingItemContainer);
